import React, { useState, useMemo } from "react";
import {
  AreaChart, Area,
  BarChart, Bar, Cell,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
  Label,
} from "recharts";

const MODE_BALANCE  = "balance";
const MODE_PAYMENTS = "payments";
const MODE_BALLOON  = "balloon";

const ACCENT          = "#635bff";
const ACCENT_LIGHT    = "rgba(99,91,255,0.12)";
const PRINCIPAL_COLOR = "#0071e3";
const INTEREST_COLOR  = "#a78bfa";
const BALLOON_COLOR   = "#f59e0b";

const MODES = [
  { id: MODE_BALANCE,  label: "יתרת קרן" },
  { id: MODE_PAYMENTS, label: "תשלומים" },
  { id: MODE_BALLOON,  label: "בלון" },
];

function formatNum(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  const abs = Math.abs(Number(n));
  if (abs >= 1_000_000) return `${(Number(n) / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${(Number(n) / 1_000).toFixed(0)}K`;
  return Number(n).toLocaleString("he-IL", { maximumFractionDigits: 0 });
}

function formatNumFull(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatDateShort(d) {
  if (!d) return "";
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString("he-IL", { month: "short", year: "2-digit" });
  } catch { return d; }
}

/* ── Tooltip ────────────────────────────────────────────────────── */
function CustomTooltip({ active, payload, currency, mode, balloonIdx }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  const dateStr = row?.label || row?.date || "";
  let displayDate = "";
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime()))
      displayDate = d.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
  } catch {}

  const isBalloonRow = balloonIdx != null && row?.periodIndex === balloonIdx;

  return (
    <div style={{
      background: "var(--bg)",
      border: `1px solid ${isBalloonRow ? BALLOON_COLOR : "var(--border-light)"}`,
      borderRadius: 10,
      padding: "10px 14px",
      boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
      fontFamily: "var(--font)",
      minWidth: 180,
      direction: "rtl",
    }}>
      {displayDate && (
        <div style={{ fontSize: 11, color: isBalloonRow ? BALLOON_COLOR : "var(--text-secondary)", fontWeight: 600, marginBottom: 7, letterSpacing: "0.03em", textTransform: "uppercase" }}>
          {displayDate}
          {isBalloonRow && " — פירעון בלון ★"}
        </div>
      )}

      {mode === MODE_BALANCE && (
        <TooltipRow label="יתרת קרן" value={`${formatNumFull(row?.balance)} ${currency}`} color={ACCENT} bold />
      )}

      {(mode === MODE_PAYMENTS || mode === MODE_BALLOON) && (
        <>
          {payload.map((entry) => {
            const isPrincipal = entry.dataKey === "principal";
            return (
              <TooltipRow
                key={entry.dataKey}
                dot={isBalloonRow ? (isPrincipal ? BALLOON_COLOR : "#fcd34d") : entry.fill}
                label={isPrincipal ? "קרן" : "ריבית"}
                value={`${formatNumFull(entry.value)} ${currency}`}
              />
            );
          })}
          {row?.payment != null && (
            <>
              <div style={{ borderTop: "1px solid var(--border-light)", margin: "6px 0" }} />
              <TooltipRow label="סה״כ תשלום" value={`${formatNumFull(row.payment)} ${currency}`} bold />
            </>
          )}
        </>
      )}
    </div>
  );
}

function TooltipRow({ label, value, color, bold, dot }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "baseline", marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        {dot && <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, display: "inline-block", flexShrink: 0 }} />}
        {label}
      </span>
      <span style={{ fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 500, color: color || "var(--text)", whiteSpace: "nowrap" }}>
        {value}
      </span>
    </div>
  );
}

function ChartLegend({ items }) {
  return (
    <div style={{ display: "flex", gap: 20, marginBottom: 14 }}>
      {items.map(({ color, label }) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Detect balloon outlier ─────────────────────────────────────── */
function detectBalloon(data) {
  if (data.length < 3) return null;
  const payments = data.map((d) => Number(d.payment) || 0);
  const sorted = [...payments].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const max = sorted[sorted.length - 1];
  if (max > median * 3) {
    const balloonIdx = payments.lastIndexOf(max);
    return { dataIdx: balloonIdx, periodIndex: data[balloonIdx]?.periodIndex, value: max, cap: median * 2.4 };
  }
  return null;
}

/* ── Shared axis props ──────────────────────────────────────────── */
const axisStyle = {
  tick: { fontSize: 11, fill: "var(--text-secondary)", fontFamily: "var(--font)" },
  tickLine: false,
  axisLine: false,
};

/* ── Main component ─────────────────────────────────────────────── */
export default function AmortizationChart({ rows, currency = "ILS", aggregated = false }) {
  const [mode, setMode] = useState(MODE_BALANCE);

  const data = useMemo(() => rows.map((row) => ({
    ...row,
    name:  row.date || `${row.periodIndex}`,
    label: row.date,
  })), [rows]);

  const balloon = useMemo(() => detectBalloon(data), [data]);

  if (!rows || rows.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextIdx  = data.findIndex((d) => d.date && new Date(d.date) >= today);
  const nextName = nextIdx >= 0 ? data[nextIdx].name : null;

  const activeMode = aggregated ? MODE_PAYMENTS : mode;

  // Generous left margin so Y-axis labels never clip
  const margin = { top: 16, right: 24, left: 8, bottom: 8 };

  const xAxis = (
    <XAxis
      dataKey="name"
      {...axisStyle}
      interval="preserveStartEnd"
      tickFormatter={formatDateShort}
      tickCount={5}
      dy={6}
    />
  );

  const yAxis = (overrides = {}) => (
    <YAxis
      {...axisStyle}
      tickFormatter={formatNum}
      width={68}
      tickCount={5}
      {...overrides}
    />
  );

  const grid = (
    <CartesianGrid strokeDasharray="0" stroke="var(--border-light)" vertical={false} strokeOpacity={0.5} />
  );

  const refLine = nextName && (
    <ReferenceLine
      x={nextName}
      stroke={ACCENT}
      strokeDasharray="4 3"
      strokeWidth={1.5}
      strokeOpacity={0.45}
    />
  );

  return (
    <div>
      {/* Mode toggle */}
      {!aggregated && (
        <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
          {MODES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              style={{
                padding: "6px 16px",
                borderRadius: 20,
                border: `1px solid ${activeMode === id ? ACCENT : "var(--border)"}`,
                background: activeMode === id ? ACCENT_LIGHT : "transparent",
                color: activeMode === id ? ACCENT : "var(--text-secondary)",
                fontSize: 12,
                fontWeight: activeMode === id ? 600 : 400,
                cursor: "pointer",
                fontFamily: "var(--font)",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ── יתרת קרן ─────────────────────────────────────── */}
      {activeMode === MODE_BALANCE && (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={margin}>
            <defs>
              <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={ACCENT} stopOpacity={0.2} />
                <stop offset="100%" stopColor={ACCENT} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            {grid}
            {xAxis}
            {yAxis()}
            {refLine}
            <Tooltip
              content={<CustomTooltip currency={currency} mode={MODE_BALANCE} />}
              cursor={{ stroke: ACCENT, strokeWidth: 1, strokeDasharray: "4 3", strokeOpacity: 0.4 }}
            />
            <Area
              type="monotone"
              dataKey="balance"
              stroke={ACCENT}
              strokeWidth={2.5}
              fill="url(#balGrad)"
              dot={false}
              activeDot={{ r: 5, fill: ACCENT, strokeWidth: 0 }}
              isAnimationActive={true}
              animationDuration={700}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {/* ── תשלומים (standard stacked) ───────────────────── */}
      {activeMode === MODE_PAYMENTS && (
        <>
          <ChartLegend items={[{ color: PRINCIPAL_COLOR, label: "קרן" }, { color: INTEREST_COLOR, label: "ריבית" }]} />
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={margin} barCategoryGap="32%">
              {grid}
              {xAxis}
              {yAxis()}
              {refLine}
              <Tooltip
                content={<CustomTooltip currency={currency} mode={MODE_PAYMENTS} />}
                cursor={{ fill: "var(--bg-subtle)", fillOpacity: 0.7 }}
              />
              <Bar dataKey="principal" stackId="p" fill={PRINCIPAL_COLOR} radius={[0, 0, 0, 0]}
                isAnimationActive animationDuration={600} animationEasing="ease-out" />
              <Bar dataKey="interest"  stackId="p" fill={INTEREST_COLOR}  radius={[4, 4, 0, 0]}
                isAnimationActive animationDuration={700} animationEasing="ease-out" />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {/* ── בלון — Y capped, balloon bar amber ───────────── */}
      {activeMode === MODE_BALLOON && (
        <>
          <ChartLegend items={[
            { color: PRINCIPAL_COLOR, label: "קרן" },
            { color: INTEREST_COLOR,  label: "ריבית" },
            { color: BALLOON_COLOR,   label: "פירעון בלון" },
          ]} />
          {balloon && (
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.5 }}>
              הציר חתוך ב-{formatNum(balloon.cap)} {currency} · פירעון הבלון ({formatNum(balloon.value)} {currency}) מסומן בנפרד
            </div>
          )}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={{ ...margin, top: 28 }} barCategoryGap="32%">
              {grid}
              {xAxis}
              {yAxis({
                domain: balloon ? [0, balloon.cap] : ["auto", "auto"],
                allowDataOverflow: true,
              })}
              {refLine}
              <Tooltip
                content={<CustomTooltip currency={currency} mode={MODE_BALLOON} balloonIdx={balloon?.periodIndex} />}
                cursor={{ fill: "var(--bg-subtle)", fillOpacity: 0.7 }}
              />
              <Bar dataKey="principal" stackId="p" radius={[0, 0, 0, 0]}
                isAnimationActive animationDuration={600} animationEasing="ease-out">
                {data.map((_, i) => (
                  <Cell key={i} fill={balloon && i === balloon.dataIdx ? BALLOON_COLOR : PRINCIPAL_COLOR} />
                ))}
              </Bar>
              <Bar dataKey="interest" stackId="p" radius={[4, 4, 0, 0]}
                isAnimationActive animationDuration={700} animationEasing="ease-out">
                {data.map((_, i) => (
                  <Cell key={i} fill={balloon && i === balloon.dataIdx ? "#fcd34d" : INTEREST_COLOR} />
                ))}
              </Bar>
              {balloon && (
                <ReferenceLine
                  y={balloon.cap}
                  stroke={BALLOON_COLOR}
                  strokeDasharray="5 4"
                  strokeWidth={1.5}
                  strokeOpacity={0.8}
                >
                  <Label
                    value={`▲ בלון: ${formatNum(balloon.value)} ${currency}`}
                    position="insideTopLeft"
                    style={{ fontSize: 11, fill: BALLOON_COLOR, fontFamily: "var(--font)", fontWeight: 600 }}
                    dy={-6}
                  />
                </ReferenceLine>
              )}
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
